import React, { useEffect, useState } from 'react';
import { X, Download, Copy, Check, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

/**
 * QR Code modal: shows the QR inline first, then lets the user download
 * OR copy their profile URL. Avoids the "blob downloaded into a file the user
 * can't open" UX problem of the old direct-download flow.
 */
export default function QRCodeModal({ open, onClose, username }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const profileUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/${username}`
    : `/${username}`;

  useEffect(() => {
    if (!open) {
      // Free the blob URL when closing
      if (imgUrl) URL.revokeObjectURL(imgUrl);
      setImgUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await fetch(`${backendUrl}/api/profile/qr`, { credentials: 'include' });
        if (!resp.ok) throw new Error('Failed to generate QR code');
        const blob = await resp.blob();
        if (cancelled) return;
        setImgUrl(URL.createObjectURL(blob));
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Failed to generate QR code');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const downloadFile = () => {
    if (!imgUrl) return;
    const a = document.createElement('a');
    a.href = imgUrl;
    a.download = `linkmonkey-${username}-qr.png`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('QR code downloaded');
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="qr-modal"
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 w-8 h-8 rounded-full hover:bg-muted/60 flex items-center justify-center transition-colors"
          aria-label="Close"
          data-testid="qr-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 sm:p-8 text-center">
          <h2 className="text-2xl font-bold mb-1">Your QR Code</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Print it, share it, slap it on a sticker. Scans straight to your LinkMonkey page.
          </p>

          {/* QR preview */}
          <div className="mx-auto w-[260px] h-[260px] rounded-xl bg-white p-4 flex items-center justify-center border border-border shadow-inner" data-testid="qr-modal-preview">
            {loading && <Loader2 className="w-8 h-8 text-zinc-400 animate-spin" />}
            {!loading && imgUrl && (
              <img src={imgUrl} alt={`QR code for @${username}`} className="w-full h-full object-contain" />
            )}
            {!loading && !imgUrl && (
              <p className="text-xs text-zinc-500">Failed to generate.</p>
            )}
          </div>

          <p className="mt-4 mb-5 text-xs font-mono text-muted-foreground break-all" data-testid="qr-modal-url">
            {profileUrl}
          </p>

          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={downloadFile}
              disabled={!imgUrl}
              className="flex flex-col items-center gap-1.5 py-3 rounded-lg border border-border bg-card hover:bg-muted/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="qr-modal-download"
            >
              <Download className="w-4 h-4" />
              <span className="text-xs font-medium">Download</span>
            </button>
            <button
              onClick={copyUrl}
              className="flex flex-col items-center gap-1.5 py-3 rounded-lg border border-border bg-card hover:bg-muted/40 transition-colors"
              data-testid="qr-modal-copy"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              <span className="text-xs font-medium">{copied ? 'Copied' : 'Copy link'}</span>
            </button>
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5 py-3 rounded-lg border border-border bg-card hover:bg-muted/40 transition-colors"
              data-testid="qr-modal-open"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="text-xs font-medium">Open</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
